import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';

import { AUTH_CONFIG, AuthManager, User } from '../config/auth.config';
import {
  syncLoginToVueApp,
  syncLogoutToVueApp,
} from '../config/cross-origin-sync';
import {
  probeVueSession,
  VueEmulationState,
} from '../config/probe-vue-session';
import { APP_ROUTES } from '../constants/routes';
import {
  applyVueEmulationIfActive,
  clearVueEndedEmulation,
} from '../utils/emulation';
import { clearAppCaches } from '../utils/clearAppCaches';
import { logger } from '../utils/logger';

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthApiResponse {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  user: User;
}

interface ValidateApiResponse {
  valid: boolean;
  user?: User;
}

interface ExternalSession {
  token: string;
  user: User;
  refreshToken?: string | null;
  /** Raw `userDetail` JSON string, mirrored verbatim from the source origin. */
  userDetail?: string | null;
  /**
   * Emulation state from a Vue-session probe. `undefined` means the caller
   * didn't probe (Google SSO, /auth/direct) — leave the stored emulation
   * keys untouched. `null` / inactive means "explicitly not emulating" —
   * clear them so a stale emulation can't leak into the adopted session.
   */
  emulation?: VueEmulationState | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: (callApi?: boolean) => Promise<void>;
  validateSession: () => Promise<boolean>;
  /**
   * Apply a session acquired outside the AuthContext (Google SSO, Vue↔React
   * handoff). Writes BOTH React state AND `AuthManager` (localStorage) in one
   * call so consumers (`PrivateRoute`, `PermissionProvider`) see
   * `isAuthenticated: true` on the next render — without this, callers that
   * write only to `AuthManager` get bounced back to `/login` by
   * `PrivateRoute` because React state still reads `null`.
   */
  applySession: (session: ExternalSession) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Returns the IndexedDB-clear promise (rather than being purely
  // fire-and-forget) so callers that redirect via `window.location.href`
  // (handleExternalLogout) can await it first — otherwise the page can
  // unload before the async `deleteDatabase` calls finish. Callers that
  // don't redirect (validateSession, the explicit `logout()` flow, which
  // navigates via react-router and never unloads the page) are free to
  // leave it unawaited.
  const clearAuthState = useCallback((): Promise<void> => {
    setUser(null);
    setToken(null);
    // Full, synchronous localStorage + sessionStorage wipe (matches Vue's
    // authStore.clearSessionDetails()) — must run BEFORE any caller does
    // AuthManager.broadcastAuthChange(), so the cross-tab sessionSync key
    // that call writes lands AFTER this clear, not before it.
    AuthManager.clearAuth();
    // Wipe persistent client caches — every IndexedDB database on this origin (matches Vue's
    // clearAllIndexedDB(), and covers the SHARED LocalCache DB too) — so cached business data
    // never outlives the session or leaks to the next user on this browser. The single clear
    // covers every logout path (explicit logout, session-invalid, cross-origin Vue-mirror) and
    // every tab (IndexedDB is per-origin/shared).
    return clearAppCaches();
  }, []);

  const validateSession = useCallback(async (): Promise<boolean> => {
    const currentToken = AuthManager.getToken();
    if (!currentToken) {
      void clearAuthState();
      return false;
    }

    try {
      // `/auth/validate` is excluded from the 401-refresh interceptor
      // (see `axios.config.ts:151-158` and Vue ERP `rw.axios.ts:87`),
      // so this call MUST go through raw `axios` — using `apiClient`
      // would still bypass refresh for this URL, which would only add
      // confusion. The error path below stays tolerant on its own.
      const response = await axios.post<ValidateApiResponse>(
        `${AUTH_CONFIG.API_BASE_URL}${AUTH_CONFIG.VALIDATE_ENDPOINT}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } },
      );

      if (!response.data.valid) {
        // Server EXPLICITLY said the session is invalid — clear it.
        void clearAuthState();
        return false;
      }
      if (response.data.user) {
        setUser(response.data.user);
        AuthManager.setUser(response.data.user);
      }
      return true;
    } catch (error) {
      // CRITICAL: do NOT clearAuthState on transport / status errors,
      // including 401 / 403. A 401 here usually means the access
      // token expired — but the refresh token is still valid, and the
      // first real `/v1/...` API call will trigger the 401-refresh
      // interceptor (which IS allowed for non-/auth/* paths) to
      // refresh the token automatically. Wiping the session here
      // would force the user to log back in even though the session
      // is actually recoverable. Mirrors Vue's behaviour: Vue's
      // `auth.store.ts` doesn't call validate on init at all — it
      // just trusts the stored token. We at least try to validate,
      // but fall through quietly when validate itself fails.
      logger.warn('Session validation skipped:', error);
      return true;
    }
  }, [clearAuthState]);

  const handleExternalLogout = useCallback(async (): Promise<void> => {
    // Await the cache clear BEFORE navigating — window.location.href
    // unloads the page, and an un-awaited async IndexedDB delete can be
    // cut off mid-flight by the navigation.
    await clearAuthState();
    // OUR OWN /login, not the Vue origin's. This runs whenever the session
    // disappeared from underneath us — most visibly when the user logs out in
    // the Vue ERP and then refreshes React, where the probe returns 'empty'.
    // Sending them to `externalVueLoginUrl()` there threw them onto the OTHER
    // app's login page and changed the origin in the address bar, so signing
    // back in landed them in Vue rather than back where they were. React has a
    // full login of its own (Google SSO + password, `pages/Login.tsx`), and the
    // explicit `logout()` below already goes to `APP_ROUTES.login` — this path
    // was the lone inconsistency.
    //
    // Still a full-document load (not react-router `navigate`): the in-memory
    // module singletons — the master-list stores in `src/stores/`, the
    // user-details store — are NOT touched by `clearAppCaches()`, so only a
    // real page load guarantees the next user can't read the previous one's
    // cached masters.
    window.location.href = APP_ROUTES.login;
  }, [clearAuthState]);

  const applySession = useCallback((session: ExternalSession): void => {
    setToken(session.token);
    setUser(session.user);
    AuthManager.setToken(session.token);
    AuthManager.setUser(session.user);
    if (session.refreshToken) {
      AuthManager.setRefreshToken(session.refreshToken);
    }
    if (session.userDetail) {
      AuthManager.setUserDetail(session.userDetail);
    }
    if (session.emulation !== undefined) {
      AuthManager.applyEmulation(session.emulation);
    }
    AuthManager.broadcastAuthChange('login');
  }, []);

  /**
   * Silent SSO pull: probe the Vue origin and, when it reports a live
   * session, adopt it wholesale (tokens + user + userDetail + emulation).
   * 'empty' / 'unknown' change nothing — the caller's normal
   * unauthenticated flow (PrivateRoute → /login) proceeds.
   */
  const adoptVueSessionIfActive = useCallback(async (): Promise<void> => {
    // Loop breaker: never probe from inside an iframe. The probed
    // artifact is static HTML (no SPA boot), but if this app is ever
    // framed it must not spawn nested probes.
    if (window.self !== window.top) return;
    const probe = await probeVueSession();
    if (probe.status === 'active' && probe.token && probe.user) {
      applySession({
        token: probe.token,
        user: probe.user,
        refreshToken: probe.refreshToken,
        userDetail: probe.userDetail,
        emulation: probe.emulation,
      });
    }
  }, [applySession]);

  const initializeAuth = useCallback(async (): Promise<void> => {
    try {
      const storedToken = AuthManager.getToken();
      const storedUser = AuthManager.getUser();
      if (!storedToken || !storedUser) {
        // No local session — pull one from the Vue origin if the user is
        // logged in there (silent SSO). Runs before `loading` flips false
        // (see finally), so PrivateRoute and PermissionProvider only
        // proceed once localStorage holds the adopted session.
        await adoptVueSessionIfActive();
        return;
      }

      setToken(storedToken);
      setUser(storedUser);

      // Mirror Vue ERP `auth.store.ts:51-58`: hydrate from localStorage
      // and TRUST the stored token on init. Vue intentionally does NOT
      // call `/auth/validate` here — instead, the first real API call's
      // 401 is what drives refresh-via-interceptor, which then retries
      // the request transparently.
      //
      // We still probe the Vue tab, but ONLY to pick up a cross-origin
      // emulation. A merely-empty Vue session does NOT trigger a proactive
      // refresh-token call any more — that fired a `/refreshtoken` request
      // on every load while Vue was signed out, which was wasteful. The
      // stored token is trusted as-is; if it has actually expired, the
      // axios 401 interceptor refreshes once on the first real API call.
      const probe = await probeVueSession();
      // Cross-origin logout (Case 2): Vue and React are different SITES, so a
      // `storage`-event broadcast from `sync-logout.html` is partitioned and
      // never reaches this tab — the probe is the only reliable cross-site
      // channel. If Vue EXPLICITLY reports no session ('empty'), mirror the
      // logout here. Gate STRICTLY on 'empty': 'unknown' means the probe
      // failed (Vue unreachable / timeout) and must NEVER force a logout.
      if (probe.status === 'empty') {
        void handleExternalLogout();
        return;
      }
      // Cross-origin emulation: if the Vue tab is emulating another user,
      // mirror that into this origin and reload so PermissionContext /
      // useCurrentUserId rebuild against the emulated identity. The same-user
      // short-circuit inside the helper makes the post-reload probe a no-op.
      if (applyVueEmulationIfActive(probe.emulation)) {
        window.location.reload();
        return;
      }
      // Vue ended a Vue-started emulation — mirror the end locally and reload
      // so PermissionContext rebuilds against the real user.
      if (clearVueEndedEmulation(probe.emulation)) {
        window.location.reload();
        return;
      }
    } catch (error) {
      // Probe failure (e.g. Vue dev server unreachable) is NOT a
      // session-level signal — leave the stored auth in place so the
      // first real API call can refresh / retry as usual.
      logger.warn('Auth initialization probe failed:', error);
    } finally {
      setLoading(false);
    }
  }, [handleExternalLogout, adoptVueSessionIfActive]);

  useEffect(() => {
    initializeAuth();

    AuthManager.setupStorageListener(
      () => void handleExternalLogout(),
      () => initializeAuth()
    );

    // Re-probe Vue when the user returns to this tab — to mirror a Vue
    // logout (Case 2), pick up an emulation started in the Vue tab while
    // we were away, or adopt a Vue session when we're signed out. A
    // merely-live Vue session never triggers a proactive `/refreshtoken`
    // call; the stored token is trusted and the axios 401 interceptor
    // refreshes on demand.
    const onFocus = async (): Promise<void> => {
      if (!AuthManager.getToken()) {
        // Parked on /login (or otherwise signed out) while the user logged
        // into Vue in another tab — adopt that session on refocus.
        await adoptVueSessionIfActive();
        return;
      }
      const probe = await probeVueSession();
      // Mirror a Vue logout that happened while we were away (Case 2). Strict
      // 'empty' gate — 'unknown' (probe failure) must not force a logout.
      if (probe.status === 'empty') {
        void handleExternalLogout();
        return;
      }
      // Pick up an emulation started in the Vue tab while we were away.
      if (applyVueEmulationIfActive(probe.emulation)) {
        window.location.reload();
        return;
      }
      // Vue ended a Vue-started emulation while we were away — mirror locally.
      if (clearVueEndedEmulation(probe.emulation)) {
        window.location.reload();
      }
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [initializeAuth, handleExternalLogout, adoptVueSessionIfActive]);

  // `login` / `logout` wrapped in `useCallback` so they're referentially
  // stable across renders — the `useMemo` `value` below depends on both,
  // and without `useCallback` they'd be fresh function instances each
  // render, defeating the `useMemo` (every consumer of `useAuth()`
  // re-renders on every parent render).
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      setLoading(true);
      try {
        const response = await axios.post<AuthApiResponse>(
          `${AUTH_CONFIG.API_BASE_URL}${AUTH_CONFIG.LOGIN_ENDPOINT}`,
          credentials,
        );
        const { refreshToken, user: userData } = response.data;
        const newToken = response.data.accessToken ?? response.data.token;
        if (!newToken) throw new Error('Server did not return an access token');

        setToken(newToken);
        setUser(userData);
        AuthManager.setToken(newToken);
        AuthManager.setUser(userData);
        if (refreshToken) AuthManager.setRefreshToken(refreshToken);
        AuthManager.broadcastAuthChange('login');

        await syncLoginToVueApp(newToken, userData, refreshToken);

        // Land on the orders listing, matching the Vue ERP's own post-login
        // destination (`ProfileLoader.vue` pushes `/orders/sales-orders/all`).
        // The Orders module is now a React route, so this is an in-app
        // navigate rather than the cross-origin document load it used to be.
        // Navigate to the FULL path, not the shorter `/orders` alias — both
        // render the same <Orders/> page, but only the full path is what
        // `ordersRouteId`'s urlPathConfiguration pattern and the "All
        // orders" menu leaf's own `route` actually match, so landing on
        // the bare alias left the aside menu's active-leaf detection
        // without a genuine URL match immediately after login.
        navigate(APP_ROUTES.ordersSalesOrdersAll);
      } catch (error) {
        logger.error('Login failed:', error);
        const message =
          (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
          'Login failed';
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    // The success path lands on the React Orders listing via react-router.
    [navigate],
  );

  const logout = useCallback(
    async (callApi: boolean = true): Promise<void> => {
      try {
        if (callApi && token) {
          try {
            await axios.post(
              `${AUTH_CONFIG.API_BASE_URL}${AUTH_CONFIG.LOGOUT_ENDPOINT}`,
              {},
              { headers: { Authorization: `Bearer ${token}` } },
            );
          } catch (error) {
            logger.warn('Logout API call failed:', error);
          }
        }
        // Not awaited: this path navigates via react-router (see the
        // `finally` block below), which never unloads the page, so there's
        // no risk of the async IndexedDB delete being cut off — unlike
        // handleExternalLogout's window.location.href redirect, which does
        // await it. Broadcasting immediately (rather than waiting on the
        // slower cache clear) keeps other tabs' logout notification fast.
        void clearAuthState();
        AuthManager.broadcastAuthChange('logout');
        await syncLogoutToVueApp();
      } catch (error) {
        logger.error('Logout error:', error);
        void clearAuthState();
        await syncLogoutToVueApp();
      } finally {
        // Explicit user logout stays inside the React app — send them to our
        // own /login page rather than bouncing to the Vue origin's login
        // (that fallback is reserved for `handleExternalLogout`, i.e. when
        // Vue reports the session is gone from underneath us).
        navigate(APP_ROUTES.login, { replace: true });
      }
    },
    [token, clearAuthState, navigate],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      isAuthenticated: !!token && !!user,
      loading,
      login,
      logout,
      validateSession,
      applySession,
    }),
    [user, token, loading, login, logout, validateSession, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
